import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';

interface CommuteZone {
  address: string;
  city?: string;
  lat: number;
  lng: number;
  radiusKm: number;
  color: string;
}

interface CustomPOI {
  label: string;
  address: string;
  lat: number;
  lng: number;
}

interface PropertyMapProps {
  listings: any[];
  center?: [number, number];
  zoom?: number;
  hoveredListingId?: number | null;
  focusedListingId?: number | null;
  onMarkerClick?: (listingId: number) => void;
  onPopupClose?: () => void;
  commuteZones?: CommuteZone[];
  /** Label for the commute radius checkbox, e.g. "30 Min." or "20 km" */
  commuteLabel?: string;
  /** User's custom points of interest (friends, family, etc.) */
  customPois?: CustomPOI[];
}

// Custom SVG house icon
function createHouseIcon(status: 'pass' | 'near'): L.DivIcon {
  const colors = {
    pass: { fill: 'rgba(99, 102, 241, 0.3)', stroke: 'rgba(165, 180, 252, 0.95)' },
    near: { fill: 'rgba(245, 158, 11, 0.25)', stroke: 'rgba(253, 230, 138, 0.95)' },
  };
  const c = colors[status] || colors.pass;

  return L.divIcon({
    className: `custom-marker custom-marker--${status}`,
    html: `<div class="realestate-marker">
      <svg viewBox="0 0 24 26" width="24" height="26">
        <path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" 
              fill="${c.fill}" stroke="${c.stroke}" stroke-width="1.5"/>
      </svg>
    </div>`,
    iconSize: [24, 26],
    iconAnchor: [12, 26],
    popupAnchor: [0, -26],
  });
}

// Abstract SVG icons for PT stops (modern, minimal, matching dark UI)
const PT_STOP_SVG: Record<string, string> = {
  // Train: stylized rail tracks / front view
  train: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="4" y="3" width="16" height="14" rx="3"/>
    <line x1="12" y1="3" x2="12" y2="17"/>
    <line x1="4" y1="10" x2="20" y2="10"/>
    <line x1="7" y1="20" x2="10" y2="17"/>
    <line x1="17" y1="20" x2="14" y2="17"/>
  </svg>`,
  // Tram: simplified tram front
  tram: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="5" y="5" width="14" height="13" rx="2"/>
    <line x1="8" y1="1" x2="8" y2="5"/>
    <line x1="16" y1="1" x2="16" y2="5"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
    <circle cx="8.5" cy="15.5" r="1"/>
    <circle cx="15.5" cy="15.5" r="1"/>
    <line x1="8" y1="18" x2="5" y2="22"/>
    <line x1="16" y1="18" x2="19" y2="22"/>
  </svg>`,
  // Bus: simplified bus side
  bus: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="4" width="18" height="14" rx="3"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <line x1="12" y1="4" x2="12" y2="10"/>
    <circle cx="7" cy="18" r="1.5"/>
    <circle cx="17" cy="18" r="1.5"/>
    <line x1="3" y1="14" x2="21" y2="14"/>
  </svg>`,
};

// Custom PT stop icon (abstract SVG in glowing circle)
function createPTStopIcon(type: 'train' | 'tram' | 'bus'): L.DivIcon {
  const config = {
    train: { color: '#8B4513', glow: 'rgba(139, 69, 19, 0.35)' },
    tram: { color: '#CD853F', glow: 'rgba(205, 133, 63, 0.35)' },
    bus: { color: '#D2B48C', glow: 'rgba(210, 180, 140, 0.35)' },
  };
  const cfg = config[type];
  const svg = PT_STOP_SVG[type];

  return L.divIcon({
    className: `pt-stop-marker pt-stop-marker--${type}`,
    html: `<div class="pt-stop-icon" style="
      color: ${cfg.color};
      background: rgba(15, 23, 42, 0.85);
      border: 2px solid ${cfg.color};
      box-shadow: 0 0 10px ${cfg.glow}, 0 0 20px ${cfg.glow};
    ">${svg}</div>`,
    iconSize: [27, 27],
    iconAnchor: [13, 13],
    popupAnchor: [0, -13],
  });
}

// School category colors (matching screenshots)
// School category colors (light blue → blue → dark blue → violet gradient)
const SCHOOL_COLORS: Record<string, string> = {
  kindergarten: '#93c5fd', // Light blue
  primarschule: '#3b82f6', // Blue
  sekundarschule: '#2563eb', // Medium-dark blue
  kantonsschule: '#8b5cf6', // Violet
  hochschule: '#6d28d9',   // Dark violet
  unclassified: '#3b0764', // Deep purple-black
};

const SCHOOL_LABELS: Record<string, string> = {
  kindergarten: 'Kindergarten',
  primarschule: 'Primarschule',
  sekundarschule: 'Sekundarschule',
  kantonsschule: 'Kantonsschule / Gymnasium / Maturitätsschule',
  hochschule: 'weitere Bildungseinrichtung',
  unclassified: 'Weitere Schule',
};

// School SVG icons per category
const SCHOOL_SVG: Record<string, string> = {
  kindergarten: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M12 12v4"/><path d="M8 20h8"/><path d="M7 16l5-4 5 4"/></svg>`,
  primarschule: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`,
  sekundarschule: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M4 4h16v13H6.5A2.5 2.5 0 0 0 4 19.5V4z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="13" y2="12"/></svg>`,
  kantonsschule: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/></svg>`,
  hochschule: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="15" y1="10" x2="15" y2="10.01"/><path d="M9 21v-4h6v4"/></svg>`,
  unclassified: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
};

// PT Stop colors by type
const PT_STOP_COLORS: Record<string, string> = {
  train: '#8B4513', // Saddle brown
  tram: '#CD853F',  // Peru / tan
  bus: '#D2B48C',   // Tan
};

const PT_STOP_LABELS: Record<string, string> = {
  train: 'Bahnhof / Zug',
  tram: 'Tram',
  bus: 'Bus',
};

// Smaller PT SVGs for legend (14x14)
const PT_STOP_SVG_SMALL: Record<string, string> = {
  train: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="3" width="16" height="14" rx="3"/><line x1="12" y1="3" x2="12" y2="17"/><line x1="4" y1="10" x2="20" y2="10"/><line x1="7" y1="20" x2="10" y2="17"/><line x1="17" y1="20" x2="14" y2="17"/></svg>`,
  tram: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="5" width="14" height="13" rx="2"/><line x1="8" y1="1" x2="8" y2="5"/><line x1="16" y1="1" x2="16" y2="5"/><line x1="5" y1="12" x2="19" y2="12"/><circle cx="8.5" cy="15.5" r="1"/><circle cx="15.5" cy="15.5" r="1"/></svg>`,
  bus: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="3"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="4" x2="12" y2="10"/><circle cx="7" cy="18" r="1.5"/><circle cx="17" cy="18" r="1.5"/></svg>`,
};

// Tax legend items (matching old PHP version: green → red gradient)
const TAX_LEGEND_ITEMS = [
  { color: '#22c55e', label: 'Tiefe Steuern (≤ 15%)' },
  { color: '#84cc16', label: 'Unterdurchschnittlich (≤ 18%)' },
  { color: '#eab308', label: 'Durchschnittlich (≤ 20%)' },
  { color: '#f59e0b', label: 'Überdurchschnittlich (≤ 22%)' },
  { color: '#f97316', label: 'Hoch (≤ 25%)' },
  { color: '#ef4444', label: 'Hohe Steuern (> 25%)' },
];

// Solar roof potential legend items (matching sonnendach.ch / BFE suitability classes)
// Warm colors = high radiation (south-facing), cool colors = low radiation (north-facing/shaded)
const SOLAR_ROOF_LEGEND_ITEMS = [
  { color: '#991b1b', label: 'Hervorragend (> 1\'400 kWh/m²/a)' },
  { color: '#f97316', label: 'Sehr gut (1\'200 – 1\'400 kWh/m²/a)' },
  { color: '#facc15', label: 'Gut (1\'000 – 1\'200 kWh/m²/a)' },
  { color: '#3b82f6', label: 'Mässig (800 – 1\'000 kWh/m²/a)' },
  { color: '#1e3a8a', label: 'Gering (< 800 kWh/m²/a)' },
];

// Health facility category colors (light green → dark green gradient)
const HEALTH_COLORS: Record<string, string> = {
  spital: '#dcfce7',     // Very light green
  arztpraxis: '#bbf7d0', // Light mint
  zahnarzt: '#86efac',   // Soft green
  apotheke: '#4ade80',   // Medium green
  pflegeheim: '#15803d', // Forest green
  therapie: '#166534',   // Dark green
  sonstige: '#134e4a',   // Dark teal-green
};

const HEALTH_LABELS: Record<string, string> = {
  spital: 'Spital / Krankenhaus',
  arztpraxis: 'Arztpraxis',
  zahnarzt: 'Zahnarzt',
  apotheke: 'Apotheke',
  pflegeheim: 'Pflegeheim / Altersheim',
  therapie: 'Therapie / Rehabilitation',
  sonstige: 'Sonstige',
};

// Health SVG icons per category (inline, for Leaflet DivIcon)
const HEALTH_SVG: Record<string, string> = {
  spital: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-4h6v4"/><line x1="12" y1="9" x2="12" y2="15"/><line x1="9" y1="12" x2="15" y2="12"/></svg>`,
  arztpraxis: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/></svg>`,
  zahnarzt: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5.5c-1.5-2-4-2.5-5.5-1S4 7 6 9.5c1.5 2 3 4 4 6.5s2 6 2 6 1-3.5 2-6 2.5-4.5 4-6.5 1.5-4 0-5.5-4-1-5.5 1z"/></svg>`,
  apotheke: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><rect x="5" y="5" width="14" height="14" rx="2"/></svg>`,
  pflegeheim: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-3-3.87"/><path d="M4 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/><path d="M8 21h8"/></svg>`,
  therapie: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 20a6 6 0 0 0-12 0"/><circle cx="12" cy="10" r="4"/><path d="M12 14v4"/><path d="M9 18h6"/></svg>`,
  sonstige: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
};

// Culture category colors (light pink → dark red)
const CULTURE_COLORS: Record<string, string> = {
  museum: '#be123c',       // Dark rose
  theater: '#e11d48',      // Rose
  kino: '#f43f5e',         // Light rose-red
  galerie: '#fb7185',      // Soft pink
  kunstzentrum: '#fda4af', // Light pink
  sonstige: '#9f1239',     // Deep red
};

const CULTURE_LABELS: Record<string, string> = {
  museum: 'Museum',
  theater: 'Theater',
  kino: 'Kino',
  galerie: 'Galerie',
  kunstzentrum: 'Kunstzentrum',
  sonstige: 'Sonstige',
};

// Culture SVG icons per category
const CULTURE_SVG: Record<string, string> = {
  museum: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/><line x1="9" y1="10" x2="9" y2="10.01"/><line x1="15" y1="10" x2="15" y2="10.01"/></svg>`,
  theater: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>`,
  kino: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg>`,
  galerie: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>`,
  kunstzentrum: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>`,
  sonstige: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
};

// Sport facility category colors (light yellow → dark yellow/amber gradient)
const SPORT_COLORS: Record<string, string> = {
  schwimmbad: '#fef9c3',     // Very light yellow
  fitnesscenter: '#fef08a',  // Light yellow
  golfplatz: '#fde047',      // Bright yellow
  tennis: '#facc15',         // Yellow
  fussballplatz: '#eab308',  // Amber-yellow
  sportzentrum: '#ca8a04',   // Dark amber
  eishalle: '#a16207',       // Brown-amber
  kletterhalle: '#854d0e',   // Dark brown-amber
  sonstige: '#713f12',       // Darkest brown
};

const SPORT_LABELS: Record<string, string> = {
  schwimmbad: 'Schwimmbad',
  fitnesscenter: 'Fitnesscenter',
  golfplatz: 'Golfplatz',
  tennis: 'Tennis',
  fussballplatz: 'Fussballplatz',
  sportzentrum: 'Sportzentrum / Stadion',
  eishalle: 'Eishalle',
  kletterhalle: 'Kletterhalle',
  sonstige: 'Sonstige',
};

const SPORT_SVG: Record<string, string> = {
  schwimmbad: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h20"/><path d="M2 16c1-1 2-2 4-2s3 1 4 2 2 2 4 2 3-1 4-2"/><path d="M12 8V4"/><circle cx="12" cy="4" r="1"/><path d="M8 10l4-2 4 2"/></svg>`,
  fitnesscenter: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 6.5h11"/><path d="M17.5 6.5v11"/><path d="M6.5 17.5h11"/><path d="M6.5 6.5v11"/><line x1="4" y1="9" x2="4" y2="15"/><line x1="20" y1="9" x2="20" y2="15"/><line x1="4" y1="12" x2="6.5" y2="12"/><line x1="17.5" y1="12" x2="20" y2="12"/></svg>`,
  golfplatz: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 18V3l7 4-7 4"/><path d="M8 21a4 4 0 0 1 8 0"/><circle cx="17" cy="17" r="2"/></svg>`,
  tennis: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M18.4 5.6a16.5 16.5 0 0 1-12.8 12.8"/><path d="M5.6 5.6a16.5 16.5 0 0 0 12.8 12.8"/></svg>`,
  fussballplatz: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/></svg>`,
  sportzentrum: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><rect x="9" y="13" width="6" height="8"/><line x1="9" y1="9" x2="15" y2="9"/></svg>`,
  eishalle: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`,
  kletterhalle: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21l4-9 4 5 4-7 4 11"/><circle cx="8" cy="8" r="1.5"/><circle cx="16" cy="5" r="1.5"/></svg>`,
  sonstige: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`,
};

// Default minimum zoom levels (will be overridden by backend settings)
const DEFAULT_ZOOM_LEVELS = {
  pt_stops: 11,   // Neighborhood level
  green: 10,      // City/town level
  schools: 9,     // Regional level
  culture: 10,
  health: 10,
  sport: 8,       // Regional level – sport facilities
  solar_roof: 14, // Building level – rooftop detail only visible when zoomed in
};

const LAYER_CONFIG: Record<string, { label: string; checked: boolean }> = {
  noise: { label: 'Lärmbelastung', checked: false },
  solar_radiation: { label: 'Sonneneinstrahlung', checked: false },
  solar_roof: { label: 'Solarpotenzial Dach', checked: false },
  green: { label: 'Grünflächen', checked: false },
  pt_stops: { label: 'ÖV-Haltestellen', checked: false },
  schools: { label: 'Schulen', checked: false },
  health: { label: 'Gesundheit', checked: false },
  sport: { label: 'Sport & Freizeit', checked: false },
  tax: { label: 'Steuersatz', checked: false },
  culture: { label: 'Kultur', checked: false },
  oldtown: { label: 'Altstadt', checked: false },
};

export default function PropertyMap({ listings, center, zoom, hoveredListingId, focusedListingId, onMarkerClick, onPopupClose, commuteZones, commuteLabel, customPois }: PropertyMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const layersRef = useRef<Record<string, L.LayerGroup | L.TileLayer>>({});
  const markerElementsRef = useRef<Record<number, HTMLElement>>({});
  const markerObjectsRef = useRef<Record<number, L.Marker>>({});
  const ptStopMarkersRef = useRef<Array<{ position: L.LatLng; type: string }>>([]);
  const ptLinesLayerRef = useRef<L.LayerGroup | null>(null);
  const commuteZonesLayerRef = useRef<L.LayerGroup | null>(null);
  const poiLayerRef = useRef<L.LayerGroup | null>(null);
  const [showCommuteZones, setShowCommuteZones] = useState<boolean>(true);
  const [showPois, setShowPois] = useState<boolean>(true);
  const [activeLayers, setActiveLayers] = useState<Record<string, boolean>>({});
  const [loadingLayers, setLoadingLayers] = useState<Record<string, boolean>>({});
  const [showSchoolLegend, setShowSchoolLegend] = useState<boolean>(false);
  const [showTaxLegend, setShowTaxLegend] = useState<boolean>(false);
  const [showHealthLegend, setShowHealthLegend] = useState<boolean>(false);
  const [showSportLegend, setShowSportLegend] = useState<boolean>(false);
  const [showCultureLegend, setShowCultureLegend] = useState<boolean>(false);
  const [showPTLegend, setShowPTLegend] = useState<boolean>(false);
  const [showSolarRoofLegend, setShowSolarRoofLegend] = useState<boolean>(false);
  const [noZoomLimit, setNoZoomLimit] = useState<Record<string, boolean>>({});
  const [showPTLines, setShowPTLines] = useState<boolean>(true);
  const [currentZoom, setCurrentZoom] = useState<number>(8);
  const [zoomLevels, setZoomLevels] = useState(DEFAULT_ZOOM_LEVELS);
  const [mapStyle, setMapStyle] = useState<{ url: string; attribution: string } | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const [layersCollapsed, setLayersCollapsed] = useState<boolean>(false);

  const hasAnyLegend = showPTLegend || showSchoolLegend || showTaxLegend || showHealthLegend || showSportLegend || showCultureLegend || showSolarRoofLegend;
  const activeLayerCount = Object.values(activeLayers).filter(Boolean).length
    + (showCommuteZones && commuteZones && commuteZones.length > 0 ? 1 : 0)
    + (showPois && customPois && customPois.length > 0 ? 1 : 0);

  const handleLayersToggle = useCallback(() => {
    setLayersCollapsed(prev => !prev);
  }, []);

  // Fetch zoom levels + map style from backend
  useEffect(() => {
    async function fetchSettings() {
      try {
        const [zoomRes, styleRes] = await Promise.all([
          fetch('/api/admin/zoom-levels'),
          fetch('/api/admin/map-style'),
        ]);
        const zoomResult = await zoomRes.json();
        if (zoomResult.success && zoomResult.data) {
          setZoomLevels(zoomResult.data);
        }
        if (zoomResult.no_zoom_limit) {
          setNoZoomLimit(zoomResult.no_zoom_limit);
        }
        const styleResult = await styleRes.json();
        if (styleResult.success && styleResult.data) {
          setMapStyle({ url: styleResult.data.url, attribution: styleResult.data.attribution });
        }
      } catch (err) {
        console.warn('Failed to fetch map settings, using defaults:', err);
      }
    }
    fetchSettings();
  }, []);

  // Initialize map (wait for map style to load)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const tileUrl = mapStyle?.url || 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
    const tileAttribution = mapStyle?.attribution || '&copy; OpenStreetMap contributors &copy; CARTO';

    const map = L.map(mapContainerRef.current, {
      center: center || [47.3769, 8.5417], // Zurich
      zoom: zoom || 8,
      zoomControl: true,
    });

    // Base tile layer (configured via admin backend)
    const tileLayer = L.tileLayer(tileUrl, {
      attribution: tileAttribution,
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);
    tileLayerRef.current = tileLayer;

    // Custom panes for z-index ordering:
    // Polygon layers (tax, green, oldtown) render below point markers
    map.createPane('polygonPane');
    map.getPane('polygonPane')!.style.zIndex = '350';

    map.createPane('pointPane');
    map.getPane('pointPane')!.style.zIndex = '450';

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Track zoom level
    setCurrentZoom(map.getZoom());
    map.on('zoomend', () => {
      setCurrentZoom(map.getZoom());
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Swap tile layer when map style changes (from admin backend)
  useEffect(() => {
    if (!mapRef.current || !mapStyle) return;
    // Remove existing tile layer and add new one
    if (tileLayerRef.current) {
      tileLayerRef.current.remove();
    }
    const newTile = L.tileLayer(mapStyle.url, {
      attribution: mapStyle.attribution,
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(mapRef.current);
    tileLayerRef.current = newTile;
  }, [mapStyle]);

  // Update markers when listings change
  useEffect(() => {
    if (!mapRef.current || !markersRef.current) return;

    markersRef.current.clearLayers();
    markerElementsRef.current = {};
    markerObjectsRef.current = {};
    const bounds: L.LatLng[] = [];

    for (const listing of listings) {
      const lat = parseFloat(listing.latitude || listing.lat);
      const lng = parseFloat(listing.longitude || listing.lng);
      if (!lat || !lng || lat === 0 || lng === 0) continue;

      const status = listing.status || 'pass';
      const icon = createHouseIcon(status);
      const marker = L.marker([lat, lng], { icon });

      // Store marker element reference
      marker.on('add', () => {
        const markerElement = marker.getElement();
        if (markerElement) {
          markerElementsRef.current[listing.id] = markerElement;
        }
      });

      // Notify parent when marker is clicked
      marker.on('click', () => {
        if (onMarkerClick) {
          onMarkerClick(listing.id);
        }
      });

      marker.on('popupclose', () => {
        if (onPopupClose) {
          onPopupClose();
        }
      });

      // Popup
      const price = listing.price_chf
        ? `CHF ${Number(listing.price_chf).toLocaleString('de-CH')}`
        : 'N/A';

      marker.bindPopup(`
        <div class="map-popup">
          <strong>${listing.title || 'Immobilie'}</strong><br/>
          <span>${listing.city || ''} ${listing.canton ? `(${listing.canton})` : ''}</span><br/>
          <span>${price} · ${listing.rooms || '?'} Zi. · ${listing.living_space_m2 || '?'} m²</span><br/>
          <span class="popup-score">Score: <strong>${listing.score || '-'}</strong></span>
          <div class="map-popup-actions">
            <a href="/listing/${listing.id}" class="map-popup-detail-btn">Detailbeschreibung</a>
            ${listing.source_url ? `<a href="${listing.source_url}" target="_blank" rel="noopener" class="map-popup-link">Zum Inserat →</a>` : ''}
          </div>
        </div>
      `);

      markerObjectsRef.current[listing.id] = marker;
      markersRef.current!.addLayer(marker);
      bounds.push(L.latLng(lat, lng));
    }

    // Auto-fit bounds
    if (bounds.length > 0) {
      mapRef.current.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 13 });
    }
  }, [listings]);

  // Draw commute radius circles on the map
  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up previous commute zones
    if (commuteZonesLayerRef.current) {
      commuteZonesLayerRef.current.clearLayers();
      mapRef.current.removeLayer(commuteZonesLayerRef.current);
      commuteZonesLayerRef.current = null;
    }

    if (!commuteZones || commuteZones.length === 0) return;
    if (!showCommuteZones) return; // User toggled off

    const group = L.layerGroup();
    const radiusLabel = commuteLabel || '';

    for (const zone of commuteZones) {
      // Draw the commute radius circle
      const circle = L.circle([zone.lat, zone.lng], {
        radius: zone.radiusKm * 1000, // Convert km to meters
        color: zone.color,
        fillColor: zone.color,
        fillOpacity: 0.08,
        weight: 2,
        opacity: 0.6,
        dashArray: '8, 6',
      });
      circle.addTo(group);

      // Add a work location marker (pin icon)
      const workIcon = L.divIcon({
        className: 'commute-work-marker',
        html: `<div class="commute-work-pin" style="--pin-color: ${zone.color}">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="${zone.color}" stroke="#fff" stroke-width="1.5">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3" fill="#fff" stroke="${zone.color}" stroke-width="1.5"/>
          </svg>
        </div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 22],
      });

      const workMarker = L.marker([zone.lat, zone.lng], { icon: workIcon, interactive: true });
      const label = zone.city || zone.address;
      const tooltipText = radiusLabel
        ? `Arbeitsort: ${label} (${radiusLabel} Radius)`
        : `Arbeitsort: ${label}`;
      workMarker.bindTooltip(tooltipText, {
        direction: 'top',
        offset: [0, -24],
        className: 'commute-work-tooltip',
      });
      workMarker.addTo(group);
    }

    group.addTo(mapRef.current);
    commuteZonesLayerRef.current = group;
  }, [commuteZones, showCommuteZones, commuteLabel]);

  // Draw custom POI markers
  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up previous POI markers
    if (poiLayerRef.current) {
      poiLayerRef.current.clearLayers();
      mapRef.current.removeLayer(poiLayerRef.current);
      poiLayerRef.current = null;
    }

    if (!customPois || customPois.length === 0 || !showPois) return;

    const group = L.layerGroup();

    for (const poi of customPois) {
      const poiIcon = L.divIcon({
        className: 'poi-custom-marker',
        html: `<div class="poi-custom-pin">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#B8860B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker([poi.lat, poi.lng], { icon: poiIcon, interactive: true });
      marker.bindTooltip(`<strong>${poi.label}</strong><br/><span class="layer-tooltip-sub">${poi.address}</span>`, {
        direction: 'top',
        offset: [0, -12],
        className: 'commute-work-tooltip',
      });
      marker.addTo(group);
    }

    group.addTo(mapRef.current);
    poiLayerRef.current = group;
  }, [customPois, showPois]);

  // Handle hover effect
  useEffect(() => {
    // Remove hover class from all markers
    Object.values(markerElementsRef.current).forEach(el => {
      el?.classList.remove('marker-hovered');
    });

    // Add hover class to hovered marker
    if (hoveredListingId && markerElementsRef.current[hoveredListingId]) {
      markerElementsRef.current[hoveredListingId].classList.add('marker-hovered');
    }
  }, [hoveredListingId]);

  // Fly to focused listing marker when card is clicked
  useEffect(() => {
    if (!focusedListingId || !mapRef.current) return;

    const marker = markerObjectsRef.current[focusedListingId];
    if (!marker) return;

    const latlng = marker.getLatLng();
    mapRef.current.flyTo(latlng, 15, { duration: 0.8 });

    // Open popup and highlight after fly animation
    setTimeout(() => {
      marker.openPopup();
      // Add highlight class (stays until popup is closed)
      const el = markerElementsRef.current[focusedListingId];
      if (el) {
        el.classList.add('marker-hovered');
      }
    }, 850);
  }, [focusedListingId]);

  // Redraw PT lines when toggle changes
  useEffect(() => {
    if (activeLayers['pt_stops']) {
      drawPTLines();
    }
  }, [showPTLines, listings]);

  // Draw connecting lines from properties to PT stops
  function drawPTLines() {
    if (!mapRef.current) return;

    // Remove existing lines
    if (ptLinesLayerRef.current) {
      mapRef.current.removeLayer(ptLinesLayerRef.current);
    }

    if (!showPTLines || ptStopMarkersRef.current.length === 0) {
      return;
    }

    const linesGroup = L.layerGroup();

    // Draw lines from each property to its nearby PT stops
    for (const listing of listings) {
      const lat = parseFloat(listing.latitude || listing.lat);
      const lng = parseFloat(listing.longitude || listing.lng);
      if (!lat || !lng) continue;

      const propertyPos = L.latLng(lat, lng);

      // Find PT stops within 1km (matching admin settings default)
      for (const stop of ptStopMarkersRef.current) {
        const distance = propertyPos.distanceTo(stop.position);
        if (distance <= 1000) {
          const line = L.polyline([propertyPos, stop.position], {
            color: PT_STOP_COLORS[stop.type as keyof typeof PT_STOP_COLORS] || '#6b7280',
            weight: 1.5,
            opacity: 0.4,
            dashArray: '5, 8',
          });
          linesGroup.addLayer(line);
        }
      }
    }

    linesGroup.addTo(mapRef.current);
    ptLinesLayerRef.current = linesGroup;
  }

  // Toggle GIS layer
  async function toggleLayer(name: string) {
    if (!mapRef.current) return;

    const isActive = activeLayers[name];

    if (isActive) {
      // Remove layer
      if (layersRef.current[name]) {
        mapRef.current.removeLayer(layersRef.current[name]);
        delete layersRef.current[name];
      }
      setActiveLayers(prev => ({ ...prev, [name]: false }));
      if (name === 'schools') setShowSchoolLegend(false);
      if (name === 'tax') setShowTaxLegend(false);
      if (name === 'health') setShowHealthLegend(false);
      if (name === 'sport') setShowSportLegend(false);
      if (name === 'culture') setShowCultureLegend(false);
      if (name === 'pt_stops') setShowPTLegend(false);
      if (name === 'solar_roof') setShowSolarRoofLegend(false);
      
      // Remove PT lines if PT stops layer is deactivated
      if (name === 'pt_stops') {
        ptStopMarkersRef.current = [];
        if (ptLinesLayerRef.current) {
          mapRef.current.removeLayer(ptLinesLayerRef.current);
          ptLinesLayerRef.current = null;
        }
      }
      return;
    }

    // Add layer
    setLoadingLayers(prev => ({ ...prev, [name]: true }));

    try {
      // SPECIAL CASE: Noise WMS Overlay (client-side only)
      if (name === 'noise') {
        const noiseLayer = L.tileLayer.wms('https://wms.geo.admin.ch/', {
          layers: 'ch.bafu.laerm-strassenlaerm_tag',
          format: 'image/png',
          transparent: true,
          opacity: 0.6,
          attribution: '© BAFU',
        }).addTo(mapRef.current!);

        layersRef.current[name] = noiseLayer;
        setActiveLayers(prev => ({ ...prev, [name]: true }));
        setLoadingLayers(prev => ({ ...prev, [name]: false }));
        return;
      }

      // SPECIAL CASE: Solar Radiation WMS Overlay (landscape-wide, no zoom restriction)
      if (name === 'solar_radiation') {
        const solarRadLayer = L.tileLayer.wms('https://wms.geo.admin.ch/', {
          layers: 'ch.bfe.solarenergie-einstrahlung_0_grad',
          format: 'image/png',
          transparent: true,
          opacity: 0.55,
          attribution: '© BFE',
        }).addTo(mapRef.current!);

        layersRef.current[name] = solarRadLayer;
        setActiveLayers(prev => ({ ...prev, [name]: true }));
        setLoadingLayers(prev => ({ ...prev, [name]: false }));
        return;
      }

      // SPECIAL CASE: Solar Roof Potential WMS Overlay (building-level, zoom restricted)
      if (name === 'solar_roof') {
        const solarRoofLayer = L.tileLayer.wms('https://wms.geo.admin.ch/', {
          layers: 'ch.bfe.solarenergie-eignung-daecher',
          format: 'image/png',
          transparent: true,
          opacity: 0.7,
          maxZoom: 18,
          attribution: '© BFE / sonnendach.ch',
        }).addTo(mapRef.current!);

        layersRef.current[name] = solarRoofLayer;
        setActiveLayers(prev => ({ ...prev, [name]: true }));
        setShowSolarRoofLegend(true);
        setLoadingLayers(prev => ({ ...prev, [name]: false }));
        return;
      }

      // Get current map bounds
      const bounds = mapRef.current.getBounds();
      const bbox: [number, number, number, number] = [
        bounds.getSouth(),
        bounds.getWest(),
        bounds.getNorth(),
        bounds.getEast(),
      ];

      // Get property positions
      const positions: [number, number][] = listings
        .map(l => {
          const lat = parseFloat(l.latitude || l.lat);
          const lng = parseFloat(l.longitude || l.lng);
          return lat && lng ? [lat, lng] : null;
        })
        .filter((p): p is [number, number] => p !== null);

      // Build API URL based on layer requirements
      let apiUrl = `/api/layers/${name}?`;
      
      if (name === 'pt_stops') {
        // PT stops only need positions
        apiUrl += `positions=${JSON.stringify(positions)}`;
      } else if (name === 'tax') {
        // Tax layer needs locations (city/zip)
        const locations = listings.map(l => ({
          city: l.city,
          zip: l.zip_code || l.zip,
        }));
        apiUrl += `bbox=${JSON.stringify(bbox)}&locations=${JSON.stringify(locations)}`;
      } else if (name === 'oldtown') {
        // Old town is now dynamic via Overpass API
        apiUrl += `bbox=${JSON.stringify(bbox)}&positions=${JSON.stringify(positions)}`;
      } else {
        // Green, schools, culture need bbox + positions
        apiUrl += `bbox=${JSON.stringify(bbox)}&positions=${JSON.stringify(positions)}`;
      }

      // Fetch layer
      const response = await fetch(apiUrl);
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to load layer');
      }

      const geojson = result.data;

      // Clear PT stop markers ref before adding new ones
      if (name === 'pt_stops') {
        ptStopMarkersRef.current = [];
      }

      // Polygon layers render in lower pane so they don't cover markers
      const isPolygonLayer = ['tax', 'green', 'oldtown', 'noise'].includes(name);
      const pane = isPolygonLayer ? 'polygonPane' : 'pointPane';

      // Create layer with proper styling
      const layerGroup = L.geoJSON(geojson, {
        pane,
        style: ['schools', 'pt_stops', 'culture', 'health'].includes(name) ? undefined : ((feature: any) => getLayerStyle(name, feature)),
        pointToLayer: (feature, latlng) => {
          // Schools with category-colored markers + SVG icons
          if (name === 'schools') {
            const category = feature.properties?.school_category || 'unclassified';
            const color = SCHOOL_COLORS[category] || SCHOOL_COLORS.unclassified;
            const svg = SCHOOL_SVG[category] || SCHOOL_SVG.unclassified;
            const icon = L.divIcon({
              className: 'school-marker',
              html: `<div style="
                width: 26px; height: 26px;
                background: rgba(15, 23, 42, 0.85);
                border: 2px solid ${color};
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 0 6px ${color}44;
                color: ${color};
              ">${svg}</div>`,
              iconSize: [26, 26],
              iconAnchor: [13, 13],
              popupAnchor: [0, -13],
            });
            return L.marker(latlng, { icon });
          }
          
          // PT stops with custom transparent icons
          if (name === 'pt_stops') {
            const type = (feature.properties?.type || 'bus') as 'train' | 'tram' | 'bus';
            // Store PT stop position for line drawing
            ptStopMarkersRef.current.push({ position: latlng, type });
            const icon = createPTStopIcon(type);
            return L.marker(latlng, { icon });
          }

          // Health facilities with category-colored markers + SVG icons
          if (name === 'health') {
            const category = feature.properties?.health_category || 'sonstige';
            const color = HEALTH_COLORS[category] || HEALTH_COLORS.sonstige;
            const svg = HEALTH_SVG[category] || HEALTH_SVG.sonstige;
            const icon = L.divIcon({
              className: 'health-marker',
              html: `<div style="
                width: 26px; height: 26px;
                background: rgba(15, 23, 42, 0.85);
                border: 2px solid ${color};
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 0 6px ${color}44;
                color: ${color};
              ">${svg}</div>`,
              iconSize: [26, 26],
              iconAnchor: [13, 13],
              popupAnchor: [0, -13],
            });
            return L.marker(latlng, { icon });
          }

          // Culture sites with category-colored markers + SVG icons
          if (name === 'culture') {
            const category = feature.properties?.culture_category || 'sonstige';
            const color = CULTURE_COLORS[category] || CULTURE_COLORS.sonstige;
            const svg = CULTURE_SVG[category] || CULTURE_SVG.sonstige;
            const icon = L.divIcon({
              className: 'culture-marker',
              html: `<div style="
                width: 26px; height: 26px;
                background: rgba(15, 23, 42, 0.85);
                border: 2px solid ${color};
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 0 6px ${color}44;
                color: ${color};
              ">${svg}</div>`,
              iconSize: [26, 26],
              iconAnchor: [13, 13],
              popupAnchor: [0, -13],
            });
            return L.marker(latlng, { icon });
          }

          // Sport facilities with category-colored markers + SVG icons
          if (name === 'sport') {
            const category = feature.properties?.sport_category || 'sonstige';
            const color = SPORT_COLORS[category] || SPORT_COLORS.sonstige;
            const svg = SPORT_SVG[category] || SPORT_SVG.sonstige;
            const icon = L.divIcon({
              className: 'sport-marker',
              html: `<div style="
                width: 26px; height: 26px;
                background: rgba(15, 23, 42, 0.85);
                border: 2px solid ${color};
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 0 6px ${color}44;
                color: ${color};
              ">${svg}</div>`,
              iconSize: [26, 26],
              iconAnchor: [13, 13],
              popupAnchor: [0, -13],
            });
            return L.marker(latlng, { icon });
          }

          // Default point style
          return L.circleMarker(latlng, {
            radius: 5,
            fillColor: getLayerColor(name),
            color: '#fff',
            weight: 1,
            fillOpacity: 0.7,
          });
        },
        onEachFeature: (feature, layer) => {
          const props = feature.properties || {};
          const tooltipOpts: L.TooltipOptions = {
            direction: 'top',
            offset: [0, -10],
            className: 'layer-tooltip',
          };
          
          if (name === 'green') {
            const typeName = props.type === 'park' ? 'Park' :
                           props.type === 'garden' ? 'Garten' :
                           props.type === 'forest' ? 'Wald' : props.type;
            const displayName = props.name_de || props.name || typeName;
            layer.bindTooltip(`<strong>${displayName}</strong><br/><span class="layer-tooltip-sub">${typeName}</span>`, tooltipOpts);
            layer.bindPopup(`<div class="layer-popup"><strong>${displayName}</strong><br/><span style="color:#94a3b8">${typeName}</span></div>`);
          } else if (name === 'schools') {
            const category = props.school_category || 'unclassified';
            const categoryLabel = SCHOOL_LABELS[category] || category;
            const color = SCHOOL_COLORS[category] || SCHOOL_COLORS.unclassified;
            const svg = SCHOOL_SVG[category] || SCHOOL_SVG.unclassified;
            const schoolName = props.name || 'Unbekannt';
            layer.bindTooltip(`<strong>${schoolName}</strong><br/><span class="layer-tooltip-sub" style="color:${color}">${categoryLabel}</span>`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup layer-popup--school">
                <div style="display: flex; justify-content: center; margin-bottom: 6px; color: ${color};">${svg}</div>
                <div class="school-category" style="color: ${color}">${categoryLabel}</div>
                <strong>${schoolName}</strong>
              </div>
            `);
          } else if (name === 'health') {
            const category = props.health_category || 'sonstige';
            const categoryLabel = HEALTH_LABELS[category] || category;
            const color = HEALTH_COLORS[category] || HEALTH_COLORS.sonstige;
            const svg = HEALTH_SVG[category] || HEALTH_SVG.sonstige;
            const healthName = props.name || 'Unbekannt';
            layer.bindTooltip(`<strong>${healthName}</strong><br/><span class="layer-tooltip-sub" style="color:${color}">${categoryLabel}</span>`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <div style="display: flex; justify-content: center; margin-bottom: 6px; color: ${color};">${svg}</div>
                <div style="color: ${color}; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem;">${categoryLabel}</div>
                <strong>${healthName}</strong>
              </div>
            `);
          } else if (name === 'sport') {
            const category = props.sport_category || 'sonstige';
            const categoryLabel = SPORT_LABELS[category] || category;
            const color = SPORT_COLORS[category] || SPORT_COLORS.sonstige;
            const svg = SPORT_SVG[category] || SPORT_SVG.sonstige;
            const sportName = props.name || 'Unbekannt';
            layer.bindTooltip(`<strong>${sportName}</strong><br/><span class="layer-tooltip-sub" style="color:${color}">${categoryLabel}</span>`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <div style="display: flex; justify-content: center; margin-bottom: 6px; color: ${color};">${svg}</div>
                <div style="color: ${color}; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem;">${categoryLabel}</div>
                <strong>${sportName}</strong>
              </div>
            `);
          } else if (name === 'pt_stops') {
            const type = props.type || 'bus';
            const ptColor = PT_STOP_COLORS[type] || PT_STOP_COLORS.bus;
            const svgIcon = PT_STOP_SVG[type] || PT_STOP_SVG.bus;
            const typeName = type === 'train' ? 'Bahnhof' : type === 'tram' ? 'Tram' : 'Bus';
            const walkMin = props.walk_minutes;
            const walkLabel = walkMin != null ? ` (${walkMin} min Fussweg)` : '';
            layer.bindTooltip(`<strong>${props.name || 'Unbekannt'}</strong><br/><span class="layer-tooltip-sub">${typeName}${walkLabel}</span>`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <div class="pt-popup-icon" style="color: ${ptColor};">${svgIcon}</div>
                <div style="color: #94a3b8; font-size: 0.75rem; text-transform: uppercase;">${typeName}</div>
                <strong>${props.name || 'Unbekannt'}</strong>
                ${walkMin != null ? `<div style="color: #94a3b8; font-size: 0.8rem; margin-top: 4px;">🚶 ${walkMin} min Fussweg</div>` : ''}
              </div>
            `);
          } else if (name === 'culture') {
            const category = props.culture_category || 'sonstige';
            const categoryLabel = CULTURE_LABELS[category] || category;
            const color = CULTURE_COLORS[category] || CULTURE_COLORS.sonstige;
            const svg = CULTURE_SVG[category] || CULTURE_SVG.sonstige;
            const cultureName = props.name || 'Unbekannt';
            layer.bindTooltip(`<strong>${cultureName}</strong><br/><span class="layer-tooltip-sub" style="color:${color}">${categoryLabel}</span>`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <div style="display: flex; justify-content: center; margin-bottom: 6px; color: ${color};">${svg}</div>
                <div style="color: ${color}; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem;">${categoryLabel}</div>
                <strong>${cultureName}</strong>
              </div>
            `);
          } else if (name === 'oldtown') {
            const displayName = props.name_de || props.name || 'Altstadt';
            const unesco = props.unesco ? ' · UNESCO' : '';
            const desc = props.description || '';
            layer.bindTooltip(`<strong>${displayName}</strong>${unesco ? `<br/><span class="layer-tooltip-sub">${unesco.trim()}</span>` : ''}`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <strong>${displayName}${unesco}</strong>
                ${desc ? `<br/><span style="color: #94a3b8; font-size: 0.85rem;">${desc}</span>` : ''}
              </div>
            `);
          } else if (name === 'tax') {
            const rate = props.tax_rate || '?';
            const municipality = props.municipality || props.gem_name || 'Unbekannt';
            const canton = props.canton || '';
            layer.bindTooltip(`<strong>${municipality}</strong><br/><span class="layer-tooltip-sub" style="color:${getTaxColor(rate)}">Steuersatz: ${rate}%</span>${canton ? `<br/><span class="layer-tooltip-sub">${canton}</span>` : ''}`, tooltipOpts);
            layer.bindPopup(`
              <div class="layer-popup">
                <div style="font-size: 1.5rem; font-weight: 700; color: ${getTaxColor(rate)}">${rate}%</div>
                <strong>${municipality}</strong>
                ${canton ? `<div style="color: #94a3b8; font-size: 0.8rem;">${canton}</div>` : ''}
              </div>
            `);
          } else if (props.name) {
            layer.bindTooltip(`<strong>${props.name}</strong>`, tooltipOpts);
            layer.bindPopup(`<div class="layer-popup"><strong>${props.name}</strong></div>`);
          }
        },
      }).addTo(mapRef.current!);

      layersRef.current[name] = layerGroup;
      setActiveLayers(prev => ({ ...prev, [name]: true }));
      if (name === 'schools') setShowSchoolLegend(true);
      if (name === 'tax') setShowTaxLegend(true);
      if (name === 'health') setShowHealthLegend(true);
      if (name === 'sport') setShowSportLegend(true);
      if (name === 'culture') setShowCultureLegend(true);
      if (name === 'pt_stops') setShowPTLegend(true);
      
      // Draw connecting lines for PT stops
      if (name === 'pt_stops') {
        // Wait a tick for markers to be added to map
        setTimeout(() => drawPTLines(), 100);
      }

    } catch (err: any) {
      console.error(`Failed to load layer ${name}:`, err);
      alert(`Fehler beim Laden der Ebene "${LAYER_CONFIG[name]?.label}": ${err.message}`);
    } finally {
      setLoadingLayers(prev => ({ ...prev, [name]: false }));
    }
  }

  return (
    <div className="map-wrapper">
      <div ref={mapContainerRef} className="map-container" />

      {/* Layer controls */}
      <div className={`layer-controls ${layersCollapsed ? 'layer-controls--collapsed' : ''}`}>
        <div
          className="layer-controls-title"
          onClick={handleLayersToggle}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          <span style={{flex:1}}>Ebenen</span>
          {layersCollapsed && activeLayerCount > 0 && (
            <span className="layer-active-count">{activeLayerCount} aktiv</span>
          )}
          <svg className={`layer-chevron ${layersCollapsed ? 'layer-chevron--collapsed' : ''}`} viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        {!layersCollapsed && (<>
        <button className="layer-clear-btn" onClick={() => {
          Object.keys(activeLayers).forEach(name => {
            if (activeLayers[name]) toggleLayer(name);
          });
          if (showCommuteZones && commuteZones && commuteZones.length > 0) {
            setShowCommuteZones(false);
          }
        }}>
          Alle löschen
        </button>
        {/* Custom POIs toggle – always visible */}
        <label
          className="layer-checkbox layer-checkbox--poi"
          onClick={(e) => {
            if (!customPois || customPois.length === 0) {
              e.preventDefault();
              window.location.href = '/profile?tab=my-places';
            }
          }}
          title={!customPois || customPois.length === 0 ? 'Klicken um besondere Orte hinzuzufügen' : ''}
        >
          <input
            type="checkbox"
            checked={showPois && (customPois?.length ?? 0) > 0}
            onChange={(e) => setShowPois(e.target.checked)}
            disabled={!customPois || customPois.length === 0}
          />
          <span>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 4, verticalAlign: 'middle'}}>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Meine Orte {customPois && customPois.length > 0 ? `(${customPois.length})` : ''}
          </span>
        </label>
        {/* Commute radius toggle – only visible when circles exist */}
        {commuteZones && commuteZones.length > 0 && (
          <>
            <label className="layer-checkbox layer-checkbox--commute">
              <input
                type="checkbox"
                checked={showCommuteZones}
                onChange={(e) => setShowCommuteZones(e.target.checked)}
              />
              <span>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 4, verticalAlign: 'middle'}}>
                  <circle cx="12" cy="12" r="10" strokeDasharray="4 3" />
                  <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
                </svg>
                Pendelradien {commuteLabel || ''}
              </span>
            </label>
          </>
        )}
        {/* Divider after personal layers */}
        <div className="layer-divider" />
        {Object.entries(LAYER_CONFIG).map(([name, cfg]) => {
          // Checkboxes disabled until proper zoom level (unless "no zoom limit" is active)
          const minZoomRequired = zoomLevels[name as keyof typeof zoomLevels];
          const hasNoZoomLimit = noZoomLimit[name] === true;
          const needsZoom = !hasNoZoomLimit && (name === 'green' || name === 'pt_stops' || name === 'health' || name === 'sport' || name === 'oldtown' || name === 'solar_roof') && minZoomRequired !== undefined && currentZoom < minZoomRequired;
          const zoomTitle = needsZoom 
            ? `Bitte zoomen Sie näher heran (min. Zoom ${minZoomRequired}) um ${cfg.label} anzuzeigen` 
            : '';

          return (
            <div key={name} className={needsZoom ? 'layer-item--needs-zoom' : ''}>
              <label 
                className={`layer-checkbox ${needsZoom ? 'layer-checkbox--disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={!!activeLayers[name]}
                  onChange={() => toggleLayer(name)}
                  disabled={loadingLayers[name] || needsZoom}
                />
                <span>{cfg.label}</span>
                {loadingLayers[name] && <span className="layer-loading"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>}
                {needsZoom && <span className="layer-zoom-tooltip">Bitte näher zoomen</span>}
              </label>
              {/* PT lines toggle directly below ÖV-Haltestellen */}
              {name === 'pt_stops' && activeLayers['pt_stops'] && (
                <div className="layer-option">
                  <label className="layer-checkbox layer-checkbox--sub">
                    <input
                      type="checkbox"
                      checked={showPTLines}
                      onChange={(e) => setShowPTLines(e.target.checked)}
                    />
                    <span>↳ Verbindungslinien</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
        </>)}
      </div>

      {/* Legends container – positioned to the left of EBENEN panel */}
      <div className={`layer-legends-container ${layersCollapsed ? 'layer-legends--ebenen-collapsed' : ''}`}>
        {/* PT Stops legend */}
        {showPTLegend && (
          <div className="layer-legend layer-legend--pt">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">ÖV-Haltestellen</div>
            {Object.entries(PT_STOP_COLORS).map(([type, color]) => (
              <div key={type} className="layer-legend-item">
                <span className="layer-legend-icon" style={{ color, borderColor: color }} dangerouslySetInnerHTML={{ __html: PT_STOP_SVG_SMALL[type] || '' }}></span>
                <span className="layer-legend-label">{PT_STOP_LABELS[type]}</span>
              </div>
            ))}
          </div>
        )}

        {/* School legend */}
        {showSchoolLegend && (
          <div className="layer-legend layer-legend--schools">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Schulen</div>
            {Object.entries(SCHOOL_COLORS).map(([category, color]) => (
              <div key={category} className="layer-legend-item">
                <span className="layer-legend-icon" style={{ color, borderColor: color }} dangerouslySetInnerHTML={{ __html: SCHOOL_SVG[category] || '' }}></span>
                <span className="layer-legend-label">{SCHOOL_LABELS[category]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tax legend */}
        {showTaxLegend && (
          <div className="layer-legend layer-legend--tax">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Steuersatz</div>
            {TAX_LEGEND_ITEMS.map(item => (
              <div key={item.label} className="layer-legend-item">
                <span className="layer-legend-swatch" style={{ backgroundColor: item.color }}></span>
                <span className="layer-legend-label">{item.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Health legend */}
        {showHealthLegend && (
          <div className="layer-legend layer-legend--health">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Gesundheit</div>
            {Object.entries(HEALTH_COLORS).map(([category, color]) => (
              <div key={category} className="layer-legend-item">
                <span className="layer-legend-icon" style={{ color, borderColor: color }} dangerouslySetInnerHTML={{ __html: HEALTH_SVG[category] || '' }}></span>
                <span className="layer-legend-label">{HEALTH_LABELS[category]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Sport legend */}
        {showSportLegend && (
          <div className="layer-legend layer-legend--sport">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Sport & Freizeit</div>
            {Object.entries(SPORT_COLORS).map(([category, color]) => (
              <div key={category} className="layer-legend-item">
                <span className="layer-legend-icon" style={{ color, borderColor: color }} dangerouslySetInnerHTML={{ __html: SPORT_SVG[category] || '' }}></span>
                <span className="layer-legend-label">{SPORT_LABELS[category]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Culture legend */}
        {showCultureLegend && (
          <div className="layer-legend layer-legend--culture">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Kultur</div>
            {Object.entries(CULTURE_COLORS).map(([category, color]) => (
              <div key={category} className="layer-legend-item">
                <span className="layer-legend-icon" style={{ color, borderColor: color }} dangerouslySetInnerHTML={{ __html: CULTURE_SVG[category] || '' }}></span>
                <span className="layer-legend-label">{CULTURE_LABELS[category]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Solar roof potential legend */}
        {showSolarRoofLegend && (
          <div className="layer-legend layer-legend--solar-roof">
            <div className="layer-legend-title">Legende</div>
            <div className="layer-legend-subtitle">Solarpotenzial Dach</div>
            {SOLAR_ROOF_LEGEND_ITEMS.map(item => (
              <div key={item.label} className="layer-legend-item">
                <span className="layer-legend-swatch" style={{ backgroundColor: item.color }}></span>
                <span className="layer-legend-label">{item.label}</span>
              </div>
            ))}
            <div className="layer-legend-source">Quelle: BFE / sonnendach.ch</div>
          </div>
        )}
      </div>
    </div>
  );
}

function getLayerStyle(name: string, feature?: any): L.PathOptions {
  switch (name) {
    case 'green':
      return {
        fillColor: '#22c55e',
        fillOpacity: 0.35,
        color: '#22c55e',
        weight: 1,
      };
    case 'oldtown':
      return {
        fillColor: '#f59e0b',
        fillOpacity: 0.15,
        color: '#f59e0b',
        weight: 2,
        dashArray: '4,4',
      };
    case 'tax':
      // Choropleth coloring based on tax rate
      const taxRate = feature?.properties?.tax_rate || 20;
      return {
        fillColor: getTaxColor(taxRate),
        fillOpacity: 0.5,
        color: '#fff',
        weight: 1,
      };
    default:
      return {
        fillColor: '#6366f1',
        fillOpacity: 0.3,
        color: '#6366f1',
        weight: 1,
      };
  }
}

function getLayerColor(name: string): string {
  switch (name) {
    case 'green': return '#22c55e';
    case 'schools': return '#ec4899';
    case 'pt_stops': return '#3b82f6';
    case 'culture': return '#ec4899';
    case 'health': return '#ef4444';
    case 'oldtown': return '#f59e0b';
    case 'tax': return '#10b981';
    default: return '#6366f1';
  }
}

/**
 * Get color for tax choropleth (green = low, red = high)
 */
function getTaxColor(rate: number): string {
  if (rate <= 15) return '#22c55e'; // Very low - green
  if (rate <= 18) return '#84cc16'; // Low - lime
  if (rate <= 20) return '#eab308'; // Medium - yellow
  if (rate <= 22) return '#f59e0b'; // Medium-high - orange
  if (rate <= 25) return '#f97316'; // High - deep orange
  return '#ef4444'; // Very high - red
}
